//import axios from 'axios';
import axios from 'axios';
import * as API from '../constants/api';
import * as CONSTANTS from '../constants/actions/app';
import { SET_PARAMETERS, SET_QUICK_FILTER } from '../constants/actions/filters';
import { SET_PARTS } from '../constants/actions/parts';
import { SET_PROMO_BANNER } from '../constants/actions/promoBanner';
import { sortNewest } from '../utilities/arrays';
import { PART_META } from '../constants/config/parameters';

/**
 * @name validateStatus
 * @function
 * @param {number} status 
 * @description Ensures only actual errors are rejected by Axios. By
 * default, Axios treats all non-200 status codes as errors.
 * 
 * For more details, see: https://github.com/axios/axios#handling-errors
 */
const validateStatus = status => ( status < 400 );

/**
 * @name getConfig
 * @function
 * @description Retrieves the column header and default filter information for the table
 * @param {string} id
 * @param {string|null} version
 */
const getConfig = ( id, version = null ) => axios.get( API.CONFIG.replace( API.ID_TOKEN, id ), { validateStatus, params: {v: version} } );

/**
 * @name getParameters
 * @function
 * @description Retrieves the parameters data for the column headers for the table
 * @param {string|null} version
 */    
const getParameters = (version = null) => axios.get( API.PARAMETERS, { validateStatus, params: {v: version} } );

/**
 * @name getVersionControl
 * @function
 * @description Retrieves the version control information for the PST
 * @param {string} id  
 * @returns 
 */
const getVersionControl = id => axios.get( API.VERSION_CONTROL.replace( API.ID_TOKEN, id ), { validateStatus,
    params: {
      _ts: Date.now() // cache buster
    }
 } );

/**
 * @name getQuickFilter
 * @function
 * @description
 * @param {string} id 
 */
const getQuickFilter = id => axios.get( API.QUICK_FILTER.replace( API.ID_TOKEN, id ), { validateStatus } );

/**
 * @name getParts
 * @function
 * @description
 * @param {string} id 
 * @param {string} type - specials | standard
 * @param {string|null} version
 */
 const getParts = (id, type = '', version = null) => axios.get( API.PARTS.replace( API.CATEGORY_TOKEN, id ).replace( API.CATEGORY_TYPE, type), { validateStatus, params: {v: version} } )

 /**
 * @name getPromoBaner
 * @function
 * @description Retrive promo banner data.
 */
 const getPromoBaner = () => axios.get(API.PROMO_BANNER, { validateStatus });

/**
 * @name handleError
 * @function
 * @description
 * @param {Object} err 
 */
const handleError = err => {
    console.error( err );

    if( !err.response ) return;

    // Handles redirects to standardized error pages
    if( err.response.status >= 400 ){
        if( err.response.status > 499 ) return window.location = API.STATUS[ '5xx' ];
        return window.location = API.STATUS[ '4xx' ];
    }

    if( !err.response && err.message === 'Network Error' ){
        err = {
            response: {
                status: 403,
                statusText: 'Cross-Origin Request Blocked'
            },
            message: 'The Same Origin Policy disallows reading the remote resource.'
        };
    }
}

/**
 * @name get
 * @constant
 * @funciton
 * @description Retrieves the PST data
 */
export const get = id => {
    return dispatch => {

        // function to get all data after version control is fetched.
        const getData = (configVersion = null, parametersVersion = null, standardVersion = null, specialVersion = null) => {
            axios
            // temporary remove quick filter - api giving error response.
            // .all([ getParameters(), getConfig( id ), getQuickFilter( id ) ])
            .all([ getParameters(parametersVersion), getConfig( id, configVersion ) ])
            .then(
                axios
                    .spread( ( params, config, quickFilters = {data: {}} ) => {
                        axios.all([ getParts( config.data.categoryId, 'standard', standardVersion ), getParts( config.data.categoryId, 'special', specialVersion ) ])
                        .then( axios.spread(
                            (partsStandard, partsSpecial) => {
                                // determine available parameters
                                let values = {},
                                // filter parameters based on user_displayable
                                parameters = [ ...config.data.columns.filter(col => col.user_displayable === "true") ],
                                keys = Object.keys( params.data ),
                                classifications = {},
                                parts = partsStandard.data.data;

                                parts = Object.values(
                                    [...partsStandard.data.data, ...partsSpecial.data.data].reduce((acc, obj) => {
                                        const rawId = obj['0'].value[ 0 ];
                                        const key = rawId.toLowerCase();
                                    
                                        acc[key] = {
                                            ...(acc[key] || {}),
                                            ...obj,
                                            id: rawId, // normalize to 'id'
                                        };
                                    
                                        delete acc[key].Id; // remove uppercase 'Id' if present
                                    
                                        return acc;
                                    }, {})
                                );
                                
                                parts.forEach( part => {

                                    let keys = Object.keys( part );
                                    
                                    keys.forEach( key => {
                                        if( !part[ key ].value || key === PART_META ) return;
                                        let value = part[ key ].value[ 0 ];                
                                        if( !values[ key ] ) values[ key ] = [];
                                        if( !values[ key ].includes( value ) ) values[ key ].push( value );
                                    });

                                });

                                
                                // build classifications as object out of the gate, and do it once
                                keys.forEach( key => {
                                    let classification = params.data[ key ].classification;
                                    if( !classifications[ classification ] ) classifications[ classification ] = [];
                                    classifications[ classification ].push( key )
                                });

                                // filter / show columns which have data.
                                parameters = parameters.filter( ( {field} ) => field !== PART_META && values[field] );

                                dispatch({ type: CONSTANTS.SET_CATEGORY, payload: { config: config.data } });

                                dispatch({ type: SET_PARTS, payload: { parts: sortNewest( parts ) } });

                                if( quickFilters.data.hasQuickFilter ) dispatch({ type: SET_QUICK_FILTER, payload: { html: quickFilters.data } });

                                dispatch({ type: SET_PARAMETERS, payload: { parameters, details: params.data, classifications, values } });
 
                            }
                        ))
                        .catch( handleError );

                    })
            )
            .catch( handleError );
        }

        // get version control info first.
        getVersionControl( id )
        .then(versionData => {
            const { parameters: parametersVersion = null, standarddata: standardVersion = null, view: configVersion = null, specialdata: specialVersion = null } = versionData.data;
            getData( configVersion, parametersVersion, standardVersion, specialVersion );
        })
        .catch( (err) => {
            console.error("Version control api issue:", err);
            // in case of any issue with version control api, proceed with getting data without versioning.
            getData();
        } );
        

        // set promoBanner details.
        // sperately called as we don't want to redirect 404 when api fails.
        getPromoBaner().then( promoBanner => {
            dispatch({ type: SET_PROMO_BANNER, payload: { details: promoBanner.data?.GetItemByIDResult } });
        })
        .catch(() => {
            console.error("Promo banner api issue.")
        });

    } 
}